import { apiClient } from './client';

// Windows browsers often report an empty `file.type` for extensions the OS
// has no registered handler for (.md, .log, …) — fall back by extension so
// the presign call always carries a real content type.
const EXT_MIME: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  log: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
};

export function effectiveMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_MIME[ext] ?? 'application/octet-stream';
}

export interface PresignedUpload {
  uploadUrl: string;
  fileUrl: string;
  path: string;
}

export interface UploadedAttachment {
  filename: string;
  url: string;
  size: number;
  mimeType: string;
  path: string;
}

export const attachmentsApi = {
  presign: async (filename: string, contentType: string, context = 'ticket'): Promise<PresignedUpload> => {
    const res = await apiClient.post('/attachments/presign', { filename, contentType, context });
    return res.data.data;
  },

  upload: async (file: File, context = 'ticket'): Promise<UploadedAttachment> => {
    const contentType = effectiveMimeType(file);
    const { uploadUrl, fileUrl, path } = await attachmentsApi.presign(file.name, contentType, context);

    // Route through apiClient (not bare fetch) so the interceptor attaches the
    // CSRF header — the browser sends the session cookie on this same-origin PUT,
    // so csrfProtection would 403 a bare fetch. uploadUrl is absolute ("/api/..."),
    // so strip the baseURL prefix before handing it to apiClient.
    await apiClient.put(uploadUrl.replace(/^\/api/, ''), file, {
      headers: { 'Content-Type': contentType },
      timeout: 120_000, // allow large files; overrides the 30s instance default
    });

    return {
      filename: file.name,
      url: fileUrl,
      size: file.size,
      mimeType: contentType,
      path,
    };
  },

  delete: async (path: string): Promise<void> => {
    await apiClient.delete('/attachments', { data: { path } });
  },
};
